// src/pages/ChatbotPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { askChatbot, fetchChatHistory, getChatDraft, saveChatDraft } from '../utils/api';
import { useAppContext } from '../context/AppContext';
import './ChatbotPage.css';

const CHAT_CACHE_NS = 'frontend';
const CHAT_CACHE_KEY = 'chatbot:history:v1';
const DRAFT_CACHE_KEY = 'chatbot:draft:v1';

const ChatbotPage = () => {
  const { token, user } = useAppContext();

  // derive a stable user identifier for history
  const userId = useMemo(() => {
    if (!user) return null;
    return user.id ?? user.user_id ?? user.uid ?? user.sub ?? user.email ?? null;
  }, [user]);

  // UI state
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]); // [{question, answer}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load history + draft on mount/user change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        // fetch server-side history if we have a user
        const raw = await fetchChatHistory(userId, token);
        // Transform role-based messages to [{question, answer}]
        const pairs = [];
        let pendingQ = null;
        for (const m of raw || []) {
          if (m.role === 'user') {
            pendingQ = m.content || '';
          } else if (m.role === 'assistant') {
            if (pendingQ !== null) {
              pairs.push({ question: pendingQ, answer: m.content || '' });
              pendingQ = null;
            }
          }
        }
        if (pendingQ !== null) {
          pairs.push({ question: pendingQ, answer: '' });
        }
        if (!cancelled) setMessages(pairs);

        // load draft (awaited to avoid [object Promise])
        const draft = await getChatDraft(userId);
        if (!cancelled) setQuestion(typeof draft === 'string' ? draft : '');
      } catch {
        // even if history fails, still load draft
        const draft = await getChatDraft(userId);
        if (!cancelled) setQuestion(typeof draft === 'string' ? draft : '');
      }
    })();
    return () => { cancelled = true; };
  }, [userId, token]);

  // Handle typing: keep a simple localStorage draft
  const onChange = (e) => {
    const val = e.target.value;
    setQuestion(val);
    try { saveChatDraft(val); } catch {}
  };

  // Submit a question
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setError('');
    const q = question.trim();

    // Optimistic UI: append the question immediately
    setMessages((prev) => [...prev, { question: q, answer: '' }]);
    // Clear input + draft
    setQuestion('');
    try { saveChatDraft(''); } catch {}

    try {
      const data = await askChatbot({ question: q, token, userId });
      const answer = (data && (data.answer || data.response || data.content)) ?? '';
      setMessages((prev) => {
        const next = [...prev];
        // fill the last unanswered pair
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].answer === '') { next[i].answer = answer; break; }
        }
        return next;
      });
    } catch (err) {
      // show error and keep the last question visible
      setError(err?.message || 'Failed to send message.');
    } finally {
      setLoading(false);
    }
  };

  // Trial lock — place AFTER hooks to keep hooks order stable
  const trialLocked = !!(user?.trial_active && !user?.is_admin);
  if (trialLocked) {
    return (
      <div className="chatbot-container">
        <h2 className="chatbot-title">AI Chatbot</h2>
        <p className="chatbot-subtitle">Feature locked during trial. Upgrade to Clairifi Core.</p>
      </div>
    );
  }

  return (
    <div className="chatbot-container">
      <h2 className="chatbot-title">AI Chatbot</h2>
      <p className="chatbot-subtitle">Ask a financial question and get instant insights.</p>

      <div className="chatbot-history">
        {messages.map((m, idx) => (
          <div className="qa" key={idx}>
            <div className="q"><strong>You:</strong> {m.question}</div>
            {m.answer !== '' && <div className="a"><strong>Clairifi:</strong> {m.answer}</div>}
          </div>
        ))}
        {messages.length === 0 && (
          <div className="qa qa-empty">No messages yet. Try asking about cash flow or runway.</div>
        )}
      </div>

      <form className="chatbot-form" onSubmit={onSubmit}>
        <input
          type="text"
          placeholder="Ask a financial question…"
          value={question}
          onChange={onChange}
          disabled={loading}
          className="chatbot-input"
        />
        <button type="submit" disabled={loading || !question.trim()} className="chatbot-submit">
          {loading ? 'Thinking...' : 'Ask'}
        </button>
      </form>

      {error && <p className="chatbot-error">{error}</p>}
    </div>
  );
};

export default ChatbotPage;
